/**
 * Password Reset Popup Component
 *
 * A modal dialog for resetting a user's password.
 * Both email and password fields are optional:
 * - If email is provided, the new password will be sent to that email.
 * - If password is provided, it will be used as the new password.
 * - If neither, a random password is generated server-side.
 */

import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AsyncButton } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Check } from 'lucide-react';

export interface PasswordResetPopupProps {
  /** Whether the popup is open */
  isOpen: boolean;
  /** User email (pre-filled, editable, optional) */
  userEmail: string;
  /** Loading state during password reset */
  isLoading?: boolean;
  /** Callback when submit is clicked */
  onSubmit: (email: string, newPassword: string) => Promise<void>;
  /** Callback when cancel is clicked */
  onCancel: () => void;
}

/**
 * Password Reset Popup
 *
 * Both email and password inputs are truly optional.
 * Messaging adapts based on what's filled in.
 */
export function PasswordResetPopup({
  isOpen,
  userEmail,
  isLoading = false,
  onSubmit,
  onCancel,
}: PasswordResetPopupProps) {
  const [email, setEmail] = useState(userEmail);
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);

  const handleSubmit = async () => {
    await onSubmit(email.trim(), password.trim());
  };

  const handleCopy = useCallback(() => {
    if (!password.trim()) return;
    navigator.clipboard.writeText(password.trim());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [password]);

  const hasEmail = email.trim().length > 0;
  const hasPassword = password.trim().length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription>
            Both fields below are optional. Leave the password blank to
            auto-generate one on the server.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Password field */}
          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <div className="flex gap-2">
              <Input
                id="new-password"
                type="text"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Leave blank to auto-generate"
                className="flex-1"
              />
              {hasPassword && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleCopy}
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                  title="Copy password"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
            {hasPassword && password.trim().length < 6 && (
              <p className="text-xs text-destructive">
                Password must be at least 6 characters.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              If left blank, the server will generate a secure password.
            </p>
          </div>

          {/* Email field */}
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Optional — send new password to this email"
            />
            <p className="text-xs text-muted-foreground">
              {hasEmail
                ? 'The new password will be sent to this email address.'
                : 'No email — the password will be updated without notification.'}
            </p>
          </div>

          {/* Summary hint */}
          <div className="rounded-md bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
            {hasEmail && hasPassword
              ? `The password you entered will be set and sent to ${email.trim()}.`
              : hasEmail && !hasPassword
                ? `A new password will be generated and sent to ${email.trim()}.`
                : hasPassword && !hasEmail
                  ? 'The password you entered will be set. No email will be sent.'
                  : 'A new password will be generated. No email will be sent.'}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <AsyncButton
            onClick={handleSubmit}
            disabled={
              (!hasEmail && !hasPassword) ||
              (hasPassword && password.trim().length < 6)
            }
            isLoading={isLoading}
            loadingText="Resetting..."
            className="transition-all duration-200 hover:translate-x-3 hover:scale-[1.02] hover:shadow-lg"
          >
            Reset Password
          </AsyncButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
